import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { getMaterialCategories } from '../../api/MaterialCategoryAPI';
import { AppThunk } from '../../app/store';
import { OptionsType, OptionType } from '../../components/filterTypes/multiselectbox/types';
import { MaterialCategoryDTO } from '../../dto/MaterialCategoryDTO';

import { updateURLQuery, getQueryArrayParam } from '../../components/QueryBuilder';

interface MaterialCategoryState {
  key: string;
  label: string;
  type: string;
  values: MaterialCategoryDTO[];
  isLoading: boolean;
  error: string | null;
  selectedIds: number[];
  selected: OptionsType | MaterialCategoryDTO[];
  isCombobox: boolean;
  inputValue: string;
  placeholder: string;
  econdaName: string;
  infoButtonDescription: string;
}

const initialState: MaterialCategoryState = {
  key: 'materialCategories',
  label: 'materialCategoryFilter',
  type: 'multiSelectChip',
  values: [],
  isLoading: false,
  error: null,
  selectedIds: getQueryArrayParam('materialCategoryIds', []),
  selected: [],
  isCombobox: false,
  inputValue: '',
  placeholder: 'filterByMaterialCategory',
  econdaName: 'material category',
  infoButtonDescription: 'infoButtonMaterialCategoryDescription',
};

function startLoading(state: MaterialCategoryState) {
  state.isLoading = true;
}

function loadingFailed(state: MaterialCategoryState, action: PayloadAction<string>) {
  state.isLoading = false;
  state.error = action.payload;
}

function updateSelectedMaterialCategories(state: MaterialCategoryState, action: PayloadAction<OptionType>) {
  state.selectedIds = [];

  const newValues: OptionType[] = JSON.parse(JSON.stringify(state.selected));
  const valueIndex = newValues.findIndex((option) => option.id === action.payload.id);

  if (valueIndex > -1) {
    newValues.splice(valueIndex, 1);
  } else {
    newValues.push(action.payload);
  }

  state.selected = newValues;

  state.selected.forEach((materialCategorySelected) => {
    state.selectedIds.push(materialCategorySelected.id);
  });

  updateURLQuery('materialCategoryIds', state.selectedIds);
}

function updateInputData(state: MaterialCategoryState, action: PayloadAction<string>) {
  state.inputValue = action.payload;
}

export const MaterialCategoriesSlice = createSlice({
  name: 'material categories',
  initialState,
  reducers: {
    getMaterialCategoriesStart: startLoading,
    getMaterialCategoriesSuccess(state, { payload }: PayloadAction<MaterialCategoryDTO[]>) {
      state.values = payload;
      state.selected = [];
      state.selectedIds.forEach((element) => {
        const selectedElement = state.values.find((value) => value.id === Number(element));
        if (selectedElement) {
          state.selectedIds.push(Number(selectedElement.id));
          state.selected.push(selectedElement);
        }
      });
      state.isLoading = false;
      state.error = null;
    },
    getMaterialCategoriesFailure: loadingFailed,
    updateMaterialCategoriesSelected: updateSelectedMaterialCategories,
    updateMaterialCategoriesInput: updateInputData,
  },
});

export const {
  getMaterialCategoriesStart,
  getMaterialCategoriesSuccess,
  getMaterialCategoriesFailure,
  updateMaterialCategoriesSelected,
  updateMaterialCategoriesInput,
} = MaterialCategoriesSlice.actions;

export default MaterialCategoriesSlice.reducer;

export const fetchMaterialCategories =
  (query: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getMaterialCategoriesStart());
      const materialCategories = await getMaterialCategories(query);
      dispatch(getMaterialCategoriesSuccess(materialCategories));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(getMaterialCategoriesFailure(err.message));
      } else {
        dispatch(getMaterialCategoriesFailure("Can't dispatch getMaterialCategories"));
      }
    }
  };
